/**
 * Hash chain del audit trail — FNV-1a 32-bit.
 *
 * Detecta tampering manual del storage (alguien edita una entry vía DevTools).
 * NO es criptográficamente seguro — un atacante con acceso al código puede
 * recalcular toda la cadena. En producción regulatoria se reemplaza por
 * HMAC-SHA256 con clave server-side (deuda documentada en Handoff GAMP 5).
 *
 * Este módulo es PURO: no toca localStorage, no sabe de React. Recibe arrays
 * inmutables y devuelve resultados. Es testeable sin mocks.
 */

import type { AuditEntry } from '../types';

/** Hash inicial de la cadena (cuando no hay entries previas). */
export const INITIAL_HASH = '00000000';

/**
 * Subset de campos que entran al cómputo del hash. Cambios fuera de esta lista
 * NO rompen la cadena (intencional: `metadatos.userAgent` puede variar entre
 * sesiones del mismo audit sin invalidar la integridad).
 */
type AuditEntryHashPayload = Pick<
  AuditEntry,
  | 'id'
  | 'timestamp'
  | 'usuarioId'
  | 'categoria'
  | 'accion'
  | 'entidadTipo'
  | 'entidadId'
  | 'valoresAntes'
  | 'valoresDespues'
  | 'razon'
> & { prevHash: string };

/**
 * Hashea una entry contra el hashPrev. Algoritmo FNV-1a 32-bit, salida hex de
 * 8 chars con padding-left de ceros para garantizar longitud constante.
 *
 * Determinístico: misma entry + mismo prevHash → mismo output siempre.
 */
export function hashAuditEntry(entry: AuditEntry, prevHash: string): string {
  const payload: AuditEntryHashPayload = {
    id: entry.id,
    timestamp: entry.timestamp,
    usuarioId: entry.usuarioId,
    categoria: entry.categoria,
    accion: entry.accion,
    entidadTipo: entry.entidadTipo,
    entidadId: entry.entidadId,
    valoresAntes: entry.valoresAntes,
    valoresDespues: entry.valoresDespues,
    razon: entry.razon,
    prevHash,
  };
  const serialized = JSON.stringify(payload);
  return fnv1a32(serialized);
}

/**
 * FNV-1a 32-bit. Constantes estándar:
 *   offset basis: 0x811c9dc5
 *   prime: 0x01000193 (implementado como sumas/shifts para evitar overflow)
 */
function fnv1a32(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    // Equivalente a `h = (h * 0x01000193) >>> 0` pero con shifts (más rápido en JS)
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/** Resultado del check de integridad sobre un audit trail completo. */
export interface IntegridadResult {
  ok: boolean;
  total: number;
  rotas: Array<{
    id: string;
    esperado: string;
    encontrado: string;
    prevEsperado: string;
    prevEncontrado: string;
  }>;
}

/**
 * Recorre el audit trail recalculando hashes y verificando la cadena.
 * Una entry queda "rota" si:
 *   - su `hashPrev` no coincide con el `hashCurrent` de la anterior, O
 *   - su `hashCurrent` recalculado no coincide con el persistido
 */
export function verificarIntegridadAuditTrail(entries: readonly AuditEntry[]): IntegridadResult {
  const rotas: IntegridadResult['rotas'] = [];
  let prevHash = INITIAL_HASH;
  for (const e of entries) {
    const recomputado = hashAuditEntry(e, prevHash);
    if (e.hashPrev !== prevHash || e.hashCurrent !== recomputado) {
      rotas.push({
        id: e.id,
        esperado: recomputado,
        encontrado: e.hashCurrent,
        prevEsperado: prevHash,
        prevEncontrado: e.hashPrev,
      });
    }
    prevHash = e.hashCurrent;
  }
  return { ok: rotas.length === 0, total: entries.length, rotas };
}
